/**
 * @file SessionDetailPage.tsx
 * @brief A session as its own page: the phone's Session Detail on the web.
 *
 * @details
 * DES (01-10-2026): "for session details, make this a page like meeting
 * preview, a page like we have on mobile app". Replaces the side sheet
 * (`SessionDetailDialog`). Port of mobile `SessionDetailScreen.tsx`:
 * - Header: back circle, "Session Detail", "Ref: …" (else the start time)
 *   and the owner; the "⋯" (delete options) on the right, which the phone
 *   does not have but a web admin needs.
 * - No cards: Date | Duration, then one pill row (language flags,
 *   Corporate / Personal, payment, status). The payment pill opens the
 *   phone's "Session cost" sheet as a menu (total, rate, base price, session
 *   ID with copy, Export invoice PDF).
 * - The full recording when there is one (web only).
 * - "Conversation (n) / History", the outline bubbles (Person A left in
 *   brand), the "END" pill; then tags and notes.
 * - A glass dock at the bottom: Transcript PDF, Audios (the phone's zip of
 *   every recording; not for a super admin, whom that route refuses; DES
 *   06-10-2026), and for a company admin the invoice preview.
 * Routes: `/company/sessions/:sessionId` (company scope) and
 * `/admin/sessions/:sessionId` (platform scope).
 *
 * @author DES
 * @date 01-10-2026
 */

import { HintBubble, HintIcon } from "@/components/ui/HintPopover";
import { useHintPopover } from "@/components/ui/useHintPopover";
import { Skeleton } from "@/components/ui/Skeleton";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Briefcase,
  Check,
  ChevronDown,
  CircleAlert,
  Copy,
  Eye,
  AudioLines,
  ArrowDownToLine,
  FileText,
  LoaderCircle,
  MessageSquare,
  Tag,
  User,
} from "lucide-react";
import { AudioPlayBadge, AudioPlayerCard } from "@/components/ui/AudioPlayer";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import { LanguageFlag } from "@/components/ui/LanguageFlag";
import { TranscriptBubbleTurn } from "@/components/ui/TranscriptBubbleTurn";
import { useCompanySessionDetail } from "@/features/company-sessions/api";
import { InvoiceMenu } from "@/components/feedback/InvoiceMenu";
import { ApiClientError, apiClient } from "@/shared/api/client";
import { invoiceEndpoints, sessionEndpoints } from "@/shared/api/endpoints";
import { toast } from "@/shared/hooks/useToast";
import { cn } from "@/shared/theme/cn";
import { dockFadeBehind, dockGlassDark, dockSelected } from "@/shared/theme/dockGlass";
import {
  formatDate,
  formatDateTime,
  formatDurationSeconds,
  formatFullName,
  formatLanguagePair,
  formatMoneyMinor,
  isRTLText,
  splitMoneyMinor,
} from "@/shared/utils/formatters";
import { useSession } from "./api";
import {
  flagInput,
  formatTs,
  paymentState,
  speakerInitials,
  SummaryItem,
} from "./components/SessionDetailDialog";
import { SessionRowActions, type SessionActionsScope } from "./components/SessionRowActions";
import { canDeleteOwnSession } from "./sessionOwnership";
import { useAuth } from "@/shared/auth/auth-context";
import { useDataControls } from "@/shared/auth/meExtras";
import { resolveSpeakerIdentity } from "./speakerIdentity";
import { SessionTagsMenu } from "./SessionTagsMenu";
import { SessionCommentMenu } from "./SessionCommentMenu";
import {
  PrivateToOwnerState,
  StaffNoContentNote,
} from "@/components/feedback/StaffNoContent";
import { isStaffNoContentError } from "@/shared/api/staffNoContent";
import { HistoryLockedState } from "@/shared/history/HistoryLockedState";
import { isHistoryLockedError } from "@/shared/history/historyLock";

/** The phone's `contextPill`: soft fill, fully round, 12px semibold. */
const pill =
  "inline-flex h-7 shrink-0 items-center gap-1 rounded-full bg-app-card px-2.5 text-xs font-semibold text-app-text-secondary";

async function downloadBlob(url: string, filename: string) {
  const res = await apiClient.get(url, { responseType: "blob" });
  const blobUrl = URL.createObjectURL(res.data as Blob);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(blobUrl);
}

export function SessionDetailPage({
  scope,
  listPath: listPathProp,
  readOnly = false,
}: {
  scope: SessionActionsScope;
  /** Where Back and delete land; defaults by scope. */
  listPath?: string;
  /** A user's own session (`/app`): no company invoice; delete only for a
   *  personal account that owns it (21-10-2026). */
  readOnly?: boolean;
}) {
  const { sessionId = "" } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const listPath = listPathProp ?? (scope === "company" ? "/company/sessions" : "/admin/sessions");
  const showInvoice = scope === "company" && !readOnly;
  const back = () => (window.history.length > 1 ? navigate(-1) : navigate(listPath));

  const platformDetail = useSession(sessionId, scope === "platform");
  const companyDetail = useCompanySessionDetail(sessionId, scope === "company");
  const detail = scope === "company" ? companyDetail : platformDetail;
  const session = detail.data;
  const canTag = scope !== "platform";
  // VERBLIZR staff (Super Admin) see metadata only: no conversation, audio,
  // comments or transcript PDF (B2/W4, DES 22-10-2026). `useSession` drops
  // the content too; this keeps the page from asking for it.
  const staffView = scope === "platform";
  // The dock's menus (Invoice, Tags, Comment): one open at a time (DES
  // 04-10-2026). The dock is pointer-events-auto, so with three independent
  // menus a second could open over the first.
  const [dockMenu, setDockMenu] = useState<"invoice" | "tags" | "comment" | null>(null);
  const dockMenuProps = (id: "invoice" | "tags" | "comment") => ({
    open: dockMenu === id,
    onOpenChange: (o: boolean) =>
      setDockMenu((cur) => (o ? id : cur === id ? null : cur)),
  });

  const [downloading, setDownloading] = useState<"transcript" | "invoice" | "audios" | null>(null);
  // ADR-051 (21-10-2026): the company turned exports off for members. The
  // invoice is billing, not meeting content, so it stays. The super admin's
  // platform view is never limited.
  const dataControls = useDataControls();
  const exportOk = scope === "platform" || dataControls.canExport;
  const [idCopied, setIdCopied] = useState(false);
  // The phone's tips icon by "Conversation History" (DES 05-10-2026).
  const tips = useHintPopover("session_detail_tips");

  const download = async (kind: "transcript" | "invoice" | "audios") => {
    setDownloading(kind);
    try {
      if (kind === "transcript") {
        if (staffView) return;
        await downloadBlob(sessionEndpoints.transcript(sessionId), `transcript_${sessionId}.pdf`);
      } else if (kind === "audios") {
        await downloadBlob(sessionEndpoints.audioZip(sessionId), `session_${sessionId}.zip`);
      } else {
        await downloadBlob(`${invoiceEndpoints.byId(sessionId)}/pdf`, `invoice_${sessionId}.pdf`);
      }
    } catch (err) {
      toast({
        variant: "destructive",
        title:
          kind === "transcript"
            ? "Transcript download failed"
            : kind === "audios"
              ? "Audio download failed"
              : "Invoice download failed",
        description: err instanceof ApiClientError ? err.message : "Please try again.",
      });
    } finally {
      setDownloading(null);
    }
  };

  const copyId = async () => {
    try {
      await navigator.clipboard.writeText(sessionId);
      setIdCopied(true);
      window.setTimeout(() => setIdCopied(false), 1500);
    } catch {
      toast({ variant: "destructive", title: "Couldn't copy the ID" });
    }
  };

  const header = (subtitle?: string, actions?: React.ReactNode) => (
    <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={back}
        aria-label="Back to sessions"
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-app-card shadow-[0_2px_8px_rgba(0,0,0,0.06)]"
      >
        <ArrowLeft className="h-5 w-5 text-app-text" />
      </button>
      <div className="min-w-0 flex-1">
        <h1 className="truncate text-[22px] font-bold text-app-text">Session Detail</h1>
        {subtitle ? (
          <p className="truncate text-[13px] font-semibold text-app-text-secondary">{subtitle}</p>
        ) : null}
      </div>
      {actions}
    </div>
  );

  // ---------------- Loading / error ----------------
  if (detail.isLoading) {
    return (
      <div className="w-full px-5 pt-6 md:px-8">
        {header()}
        {/* Skeleton in the page's shape (web-only, DES 01-10-2026): Date |
            Duration, the pill row, the Conversation heading and turns. */}
        <div role="status" aria-label="Loading session" className="mt-6 animate-pulse">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-2">
              <Skeleton still className="h-3 w-10" />
              <Skeleton still className="h-4 w-32" />
            </div>
            <div className="flex flex-col items-end space-y-2">
              <Skeleton still className="h-3 w-14" />
              <Skeleton still className="h-4 w-16" />
            </div>
          </div>
          <div className="mt-4 flex items-center gap-2">
            <Skeleton still className="h-7 w-7 rounded-full" />
            <Skeleton still className="-ml-4 h-7 w-7 rounded-full ring-2 ring-app-page" />
            <Skeleton still className="h-7 w-24 rounded-full" />
            <Skeleton still className="h-7 w-20 rounded-full" />
            <Skeleton still className="h-7 w-24 rounded-full" />
          </div>
          <Skeleton still className="mb-5 mt-8 h-6 w-36" />
          {[
            ["mr-auto", "w-[72%]"],
            ["ml-auto", "w-[60%]"],
            ["mr-auto", "w-[52%]"],
            ["ml-auto", "w-[66%]"],
          ].map(([side, w], i) => (
            <div key={i} className={`mb-4 ${side} ${w}`}>
              <Skeleton still className="mb-2 h-3 w-20" />
              <Skeleton still className="h-16 w-full rounded-[20px]" />
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (detail.error || !session) {
    return (
      <div className="w-full px-5 pt-6 md:px-8">
        {header()}
        {isStaffNoContentError(detail.error) ? (
          <PrivateToOwnerState className="mt-10" />
        ) : isHistoryLockedError(detail.error) ? (
          // B39: older than this user's history window (403 HISTORY_LOCKED).
          <HistoryLockedState error={detail.error} onBack={back} />
        ) : (
        <div className="mt-16 flex flex-col items-center gap-4 text-center">
          <CircleAlert className="h-14 w-14 text-destructive" strokeWidth={1.5} />
          <p className="text-[15px] font-medium text-app-text-secondary">
            {detail.error instanceof Error ? detail.error.message : "No session data found."}
          </p>
          <div className="flex gap-2">
            {detail.error ? (
              <button
                type="button"
                onClick={() => void detail.refetch()}
                className="h-11 min-w-[120px] rounded-full bg-brand px-5 text-sm font-semibold text-white hover:bg-brand/90"
              >
                Retry
              </button>
            ) : null}
            <button
              type="button"
              onClick={back}
              className="h-11 min-w-[120px] rounded-full bg-app-quaternary px-5 text-sm font-semibold text-app-text hover:bg-app-quaternary-dark"
            >
              Back
            </button>
          </div>
        </div>
        )}
      </div>
    );
  }

  // ---------------- The page ----------------
  const owner =
    formatFullName(session.owner_first_name, session.owner_last_name) !== "—"
      ? formatFullName(session.owner_first_name, session.owner_last_name)
      : (session.owner_email ?? null);
  const subtitle = [
    session.reference_number
      ? `Ref: ${session.reference_number}`
      : session.started_at
        ? formatDateTime(session.started_at)
        : null,
    owner,
    session.company_name,
  ]
    .filter(Boolean)
    .join(" · ");

  const billingContext = (session as { billing_context?: string | null }).billing_context;
  const isCorporate = !!session.company_id || billingContext === "corporate";
  const currency = session.currency ?? "GBP";
  const [moneySymbol, moneyAmount] = splitMoneyMinor(session.total_cents, currency);
  const payment = paymentState(session);
  const utterances = session.utterances ?? [];
  // The phone's Audios: only when there is a recording to zip (none after
  // "Delete audio", or when Person B declined).
  const showAudios =
    scope === "company" &&
    (!!session.audio_url || utterances.some((u) => u.src_audio_url || u.tgt_audio_url));
  const status = session.status
    ? session.status.charAt(0).toUpperCase() + session.status.slice(1).replace(/_/g, " ")
    : "Unknown";

  return (
    <div className="flex min-h-[calc(100dvh-5rem)] w-full flex-col px-5 pb-6 pt-6 md:px-8">
      {header(
        subtitle,
        session.status !== "deleted" && (!readOnly || canDeleteOwnSession(user, session)) ? (
          <SessionRowActions sessionId={sessionId} scope={scope} onAllDeleted={() => navigate(listPath)} />
        ) : null,
      )}

      {staffView ? <StaffNoContentNote className="mt-3" /> : null}

      <div className="mt-6">
        {/* Date | Duration, the phone's meta row. */}
        <div className="flex items-start justify-between gap-4">
          <SummaryItem label="Date" value={session.started_at ? formatDate(session.started_at) : "—"} />
          <SummaryItem align="end" label="Duration" value={formatDurationSeconds(session.duration_seconds)} />
        </div>

        {/* One pill row: flags, billing, payment (opens the cost), status. */}
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {session.source_language || session.target_language ? (
            <span
              className="inline-flex items-center"
              title={formatLanguagePair(session.source_language, session.target_language)}
            >
              <LanguageFlag language={flagInput(session.source_language)} className="h-7 w-7" />
              <LanguageFlag
                language={flagInput(session.target_language)}
                className="-ml-2 h-7 w-7 ring-2 ring-app-page"
              />
            </span>
          ) : null}
          <span className={cn(pill, isCorporate && "bg-brand/[0.08] text-brand dark:bg-brand/[0.16]")}>
            {isCorporate ? <Briefcase className="h-3 w-3" /> : <User className="h-3 w-3" />}
            {isCorporate ? "Corporate" : "Personal"}
          </span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                title="Session cost"
                className={cn(
                  pill,
                  "transition-colors",
                  payment?.paid
                    ? "bg-[#E8F5E9] text-[#2E7D32] dark:bg-[rgba(46,125,50,0.22)] dark:text-[#86EFAC]"
                    : "hover:text-app-text",
                )}
              >
                {payment?.label ?? "Cost"}
                <ChevronDown className="h-3 w-3" />
              </button>
            </DropdownMenuTrigger>
            {/* The phone's "Session cost" sheet, as a menu. */}
            <DropdownMenuContent align="start" className="w-80 p-4">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[13px] font-semibold text-app-text-secondary">Total cost</span>
                {payment ? (
                  <span className="text-xs font-semibold text-app-text-secondary">{payment.label}</span>
                ) : null}
              </div>
              <p className="mt-1 text-4xl font-bold tracking-[-0.8px]">
                <span className="text-brand">{moneySymbol}</span>
                <span className="text-app-text">{moneyAmount}</span>
              </p>
              {session.rate_per_min_cents != null || session.min_charge_cents != null ? (
                <div className="mt-4 flex items-start justify-between gap-4">
                  <SummaryItem
                    label="Rate"
                    value={
                      session.rate_per_min_cents != null
                        ? `${formatMoneyMinor(session.rate_per_min_cents, currency)}/min`
                        : "—"
                    }
                  />
                  <SummaryItem
                    align="end"
                    label="Base price"
                    value={formatMoneyMinor(session.min_charge_cents, currency)}
                  />
                </div>
              ) : null}
              <button
                type="button"
                onClick={() => void copyId()}
                className="group mt-4 flex w-full min-w-0 flex-col items-start gap-1 text-left"
              >
                <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                  Session ID
                </span>
                <span className="flex w-full min-w-0 items-center gap-2">
                  <span className="break-all font-mono text-[13px] text-app-text">{sessionId}</span>
                  {idCopied ? (
                    <Check className="h-4 w-4 shrink-0 text-green-600" />
                  ) : (
                    <Copy className="h-4 w-4 shrink-0 text-muted-foreground group-hover:text-app-text" />
                  )}
                </span>
              </button>
              {showInvoice ? (
                <>
                  <DropdownMenuSeparator className="my-3" />
                  <DropdownMenuItem onSelect={() => void download("invoice")}>
                    <ArrowDownToLine className="h-4 w-4" /> Export invoice PDF
                  </DropdownMenuItem>
                </>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
          <span className={pill}>{status}</span>
        </div>

        {!staffView && session.audio_url ? (
          <div className="mt-6 rounded-[20px] bg-app-card p-4">
            <AudioPlayerCard
              src={session.audio_url}
              title="Full conversation"
              keyboardShortcuts
              subtitle={
                session.duration_seconds
                  ? `Mixed stereo · ${formatDurationSeconds(session.duration_seconds)}`
                  : undefined
              }
              className="rounded-none border-0 bg-transparent p-0"
            />
          </div>
        ) : null}

        {staffView ? (
          <>
            <h2 className="mb-5 mt-8 text-xl font-bold leading-[26px] text-app-text">Conversation</h2>
            <PrivateToOwnerState
              withLine={false}
              detail={[
                `${Number(session.utterance_count ?? 0)} turns`,
                session.has_audio ? "audio kept" : "no audio",
                session.has_transcript ? "transcript PDF made" : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            />
          </>
        ) : (
          <>
        {/* "Conversation (n)" / "History", the phone's heading, with the
            tips icon beside "History" so its line stays short. */}
        <div className="mt-8 flex items-end gap-2">
          <h2 className="mb-5 min-w-0 flex-1 text-xl font-bold leading-[26px] text-app-text">
            <span className="inline-flex items-center">
              Conversation
              <span className="ml-2 rounded-full bg-brand/10 px-2.5 py-1 text-sm font-bold leading-none text-brand">
                {utterances.length}
              </span>
            </span>
            <span className="block">History</span>
          </h2>
          <HintIcon open={tips.open} onClick={tips.toggle} anchorRef={tips.anchorRef} className="mb-4" />
        </div>
        {tips.open ? (
          <HintBubble
            lines={[
              `Click ${payment?.label ?? "Cost"} to see the session cost and ID; click the ID to copy it.`,
              ...(session.audio_url
                ? ["Space plays or pauses the recording; ← and → skip 10 seconds, with Shift 30."]
                : []),
            ]}
            onClose={tips.close}
            anchorRef={tips.anchorRef}
            className="-mt-2 mb-5"
          />
        ) : null}

        {utterances.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-16 text-center opacity-50">
            <MessageSquare className="h-12 w-12 text-app-text-secondary opacity-40" />
            <p className="text-[15px] text-app-text-secondary">No utterances recorded in this session.</p>
          </div>
        ) : (
          <>
            <ol className="m-0 flex list-none flex-col gap-2 p-0">
              {utterances.map((u, idx) => {
                const { name, isPersonA: isA, isKnownPair } = resolveSpeakerIdentity(u.speaker, session);
                return (
                  <TranscriptBubbleTurn
                    key={idx}
                    isA={isA}
                    name={name}
                    avatarUrl={isA ? session.person_a_avatar_url : null}
                    gender={isKnownPair ? (isA ? session.person_a_gender : session.person_b_gender) : null}
                    initials={speakerInitials(name)}
                    meta={formatTs(u.timestamp_ms, session.started_at)}
                  >
                    {u.original_text ? (
                      <p
                        dir={isRTLText(u.original_text) ? "rtl" : "ltr"}
                        className="whitespace-pre-line break-words text-[17px] leading-[1.45] text-app-text"
                      >
                        {u.original_text}
                      </p>
                    ) : null}
                    {u.translated_text ? (
                      <p
                        dir={isRTLText(u.translated_text) ? "rtl" : "ltr"}
                        className="mt-2 whitespace-pre-line break-words text-[15px] leading-relaxed text-app-text-secondary opacity-70"
                      >
                        {u.translated_text}
                      </p>
                    ) : null}
                    {u.src_audio_url || u.tgt_audio_url ? (
                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        <AudioPlayBadge src={u.src_audio_url} variant="original" label="Original" />
                        <AudioPlayBadge src={u.tgt_audio_url} variant="translated" label="Translated" />
                      </div>
                    ) : null}
                  </TranscriptBubbleTurn>
                );
              })}
            </ol>
            {/* The phone's end of the conversation. */}
            <div className="mt-4 flex w-[50px] justify-center">
              <span className="rounded-[14px] bg-brand px-2.5 py-[5px] text-[10px] font-bold tracking-[1.5px] text-white">
                END
              </span>
            </div>
          </>
        )}
          </>
        )}

        {/* Tags and comments are shown here and edited from the dock's Tags /
            Comment buttons (DES 04-10-2026). */}
        {session.tags && session.tags.length > 0 ? (
          <>
            <h2 className="mb-3 mt-10 text-lg font-semibold tracking-[-0.5px] text-app-text/40">Tags</h2>
            <div className="flex flex-wrap items-center gap-1.5">
              {session.tags.map((t) => (
                <span key={t} className={pill}>
                  {t}
                </span>
              ))}
            </div>
          </>
        ) : null}

        {!staffView && session.notes ? (
          <>
            <h2 className="mb-3 mt-10 text-lg font-semibold tracking-[-0.5px] text-app-text/40">
              Comments
            </h2>
            <p
              dir={isRTLText(session.notes) ? "rtl" : "ltr"}
              className="whitespace-pre-wrap text-[15px] leading-relaxed text-app-text"
            >
              {session.notes}
            </p>
          </>
        ) : null}
      </div>

      {/* No dock for VERBLIZR staff: its buttons are all content (transcript,
          audio) or member-only (invoice, tags, comment). */}
      {staffView ? null : (
        <>
      {/* The phone's glass dock. Sticky in the page's flow like the
          Boardrooms and Notes docks, so it stops above the shell footer; it
          was `fixed` and slid under it (DES 01-10-2026). */}
      {/* THE dock fade behind it; in dark THE dock glass (DES 08-10-2026). */}
      <div className={cn("pointer-events-none sticky bottom-6 z-30 mt-auto flex flex-col items-center gap-2 px-4 pt-8", dockFadeBehind)}>
        {exportOk ? null : (
          <p className="pointer-events-auto text-center text-[13px] text-muted-foreground">
            {dataControls.exportLine}
          </p>
        )}
        <div
          className={cn(
            "pointer-events-auto flex h-14 items-center gap-1 rounded-full border border-black/5 bg-app-card/80 px-2 shadow-[0_8px_30px_rgba(0,0,0,0.12)] backdrop-blur-xl",
            dockGlassDark,
          )}
        >
          <button
            type="button"
            disabled={downloading !== null || !exportOk}
            title={exportOk ? undefined : (dataControls.exportLine ?? undefined)}
            onClick={() => void download("transcript")}
            // THE selected dock pill (U3): white on brand 72% in dark.
            className={cn("inline-flex h-10 items-center gap-2 rounded-full px-4 text-sm font-semibold disabled:opacity-60", dockSelected)}
          >
            {downloading === "transcript" ? (
              <LoaderCircle className="h-5 w-5 animate-spin" />
            ) : (
              <FileText className="h-5 w-5" />
            )}
            {downloading === "transcript" ? "Preparing…" : "Transcript PDF"}
          </button>
          {showAudios ? (
            <button
              type="button"
              disabled={downloading !== null || !exportOk}
              title={exportOk ? undefined : (dataControls.exportLine ?? undefined)}
              onClick={() => void download("audios")}
              className="inline-flex h-10 items-center gap-2 rounded-full px-4 text-sm font-semibold text-app-text-secondary hover:bg-black/[0.04] hover:text-app-text disabled:opacity-60 dark:hover:bg-white/[0.06]"
            >
              {downloading === "audios" ? (
                <LoaderCircle className="h-5 w-5 animate-spin" />
              ) : (
                <AudioLines className="h-5 w-5" />
              )}
              {downloading === "audios" ? "Preparing…" : "Audios"}
            </button>
          ) : null}
          {showInvoice ? (
            <InvoiceMenu
              invoiceId={sessionId}
              status={payment?.paid ? "paid" : null}
              {...dockMenuProps("invoice")}
              align="center"
              side="top"
              // Measured from the button, which sits 8px inside the dock: ~20px of
              // clear air above the dock glass (DES 04-10-2026).
              sideOffset={28}
            >
              <button
                type="button"
                className="inline-flex h-10 items-center gap-2 rounded-full px-4 text-sm font-semibold text-app-text-secondary hover:bg-black/[0.04] hover:text-app-text dark:hover:bg-white/[0.06]"
              >
                <Eye className="h-5 w-5" />
                Invoice
              </button>
            </InvoiceMenu>
          ) : null}
          {/* Tags and a comment from the dock (DES 04-10-2026: "there is no
              way to add them other than from the home cards"). Dropdowns,
              as everywhere; not for a super admin, whom the routes refuse. */}
          {canTag ? (
            <>
              <SessionTagsMenu
                sessionId={sessionId}
                tags={session.tags ?? []}
                {...dockMenuProps("tags")}
                align="center"
                side="top"
                sideOffset={28} // = InvoiceMenu beside it: ~20px above the dock
                anchor={
                  <button type="button" className="inline-flex h-10 items-center gap-2 rounded-full px-4 text-sm font-semibold text-app-text-secondary hover:bg-black/[0.04] hover:text-app-text dark:hover:bg-white/[0.06]">
                    <Tag className={cn("h-5 w-5", session.tags?.length ? "text-brand" : undefined)} />
                    Tags
                  </button>
                }
              />
              <SessionCommentMenu
                sessionId={sessionId}
                notes={session.notes}
                {...dockMenuProps("comment")}
                align="center"
                side="top"
                sideOffset={28} // = InvoiceMenu beside it: ~20px above the dock
                anchor={
                  <button type="button" className="inline-flex h-10 items-center gap-2 rounded-full px-4 text-sm font-semibold text-app-text-secondary hover:bg-black/[0.04] hover:text-app-text dark:hover:bg-white/[0.06]">
                    <MessageSquare
                      className={cn("h-5 w-5", session.notes ? "text-brand" : undefined)}
                    />
                    Comment
                  </button>
                }
              />
            </>
          ) : null}
        </div>
      </div>
        </>
      )}

    </div>
  );
}

export default SessionDetailPage;
